// routes/courseLoaders.ts
//
// `/course/session` needs a module in the store before it paints. Arriving
// from Start, there is one; arriving cold (a reload, a typed URL), the loader
// starts the next module itself, and sends a finished course back to the
// start screen (the desktop; narrow widths forward to the Dashboard).
//
// An address that names a step (`courseSessionStepPath`, the way back from a
// module quiz's self-assessment pages) opens that step instead, read fresh so
// it shows what happened while the student was away, and then drops the
// query so a later reload resumes as usual.
//
// A cold load (an LMS opening the step in a new tab) runs the loader before
// redux-persist has rehydrated the profile, so the loader waits for it first;
// a tab that signed in without loading the profile yet (the LMS Connect
// landing) asks the server, as `desktopLandingLoader` does.

import { redirect, type LoaderFunctionArgs } from 'react-router-dom';
import { isCourseStudentProfile } from '../instances/useInstance';
import { peekUserProfile } from '../services/auth';
import { store } from '../store/store';
import { waitForRehydration } from '../store/waitForRehydration';
import {
  openCourseStepAt,
  startNextModule,
} from '../slices/courseSession/courseSessionSlice';
import {
  COURSE_SESSION_PATH,
  readCourseSessionTarget,
} from './courseSessionPath';

export const COURSE_START_PATH = '/';

const readProfile = async () => {
  const { auth, user } = store.getState();
  if (user.profile || !auth.isAuthenticated) return user.profile;

  return (await peekUserProfile({ reuseRecent: true })) ?? null;
};

export const courseSessionLoader = async ({ request }: LoaderFunctionArgs) => {
  await waitForRehydration();
  const state = store.getState();

  // Course students only: a default user who types the URL goes to their
  // desktop without a course request being made for them.
  if (!isCourseStudentProfile(await readProfile())) {
    return redirect(COURSE_START_PATH);
  }

  const target = readCourseSessionTarget(new URL(request.url).searchParams);

  if (target) {
    try {
      const result = await store.dispatch(openCourseStepAt(target)).unwrap();
      if (!result.module) return redirect(COURSE_START_PATH);
    } catch {
      return redirect(COURSE_START_PATH);
    }

    return redirect(COURSE_SESSION_PATH);
  }

  if (state.courseSession.module) return null;

  try {
    const result = await store.dispatch(startNextModule()).unwrap();
    if (!result.module) return redirect(COURSE_START_PATH);
  } catch {
    return redirect(COURSE_START_PATH);
  }

  return null;
};
